import i18n from '@/utils/i18n';
import React from 'react';
import { gql, useMutation } from '@apollo/client';
import { ORDER_GUIDE_RESTAURANT_COLLECTION_FRAGMENT } from '@/contexts/items-context-queries';
import {
  OrderGuide_CreateCollectionMutation,
  OrderGuide_CreateCollectionMutationVariables,
} from '@/types';
import { OrderGuideActionSheetNameInput } from './order-guide-action-sheet-name-input';
import { useOrderGuideItemsContext } from '@/contexts/order-guide-items-context';
import { useOrderGuideStoreApi } from '@/stores/order-guide-store';
import { useToasts } from '@/contexts/toast-context';
import { useUserContext } from '@/contexts/user-context';
import { validateCollectionName } from '@/utils/order-guide';

interface Props {
  inputError: string;
  onCreate: (collectionUUID: string) => void;
  saveButtonText: string;
  setInputError: React.Dispatch<React.SetStateAction<string>>;
}

export const CREATE_COLLECTION_MUTATION = gql`
  mutation OrderGuide_CreateCollection(
    $name: String!
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
  ) {
    createOrderGuideCollection(
      name: $name
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
    ) {
      order_guide_restaurant_collection {
        ...ItemsContext_OrderGuideRestaurantCollectionFields
      }
    }
  }

  ${ORDER_GUIDE_RESTAURANT_COLLECTION_FRAGMENT}
`;

export const AddCollectionContent: React.FC<Props> = ({
  inputError,
  onCreate,
  saveButtonText,
  setInputError,
}) => {
  const { collections } = useOrderGuideItemsContext();
  const orderGuideStoreApi = useOrderGuideStoreApi();
  const { selectedRestaurantRelationship } = useUserContext();
  const { showErrorToast } = useToasts();

  const [createCollection, { loading: isCreating }] = useMutation<
    OrderGuide_CreateCollectionMutation,
    OrderGuide_CreateCollectionMutationVariables
  >(CREATE_COLLECTION_MUTATION);
  return (
    <OrderGuideActionSheetNameInput
      inputError={inputError}
      isSaving={isCreating}
      nameInputLabel={i18n.t('order_guide_name')}
      onChangeText={() => setInputError('')}
      onSave={async (name) => {
        if (
          !validateCollectionName(
            name,
            collections.map((c) => c.name),
          )
        ) {
          setInputError(i18n.t('order_guide_name_error'));
          return;
        }
        try {
          const result = await createCollection({
            variables: {
              name,
              restaurantUUID: selectedRestaurantRelationship.uuid,
              supplierUUID: selectedRestaurantRelationship.supplierUUID,
            },
          });

          const collection =
            result.data?.createOrderGuideCollection
              .order_guide_restaurant_collection;

          if (collection) {
            orderGuideStoreApi.getState().addRestaurantCollection(collection);
            onCreate(collection.uuid);
          }
        } catch (e) {
          showErrorToast();
        }
      }}
      saveButtonText={saveButtonText}
    />
  );
};
